/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { toOptions } from '../../utils/ManagerUtils';
import { defaultValue } from './ChartJSTreemap';
import { page as SamplePage } from '../../help/sample_pages/TreemapPage';

const ChartJSTreemapManager: ComponentManagerFn<'ChartJSTreemap'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ChartJSTreemap'>(platform, 'ChartJSTreemap'),
    type: 'ChartJSTreemap',
    name: 'Chart JS Treemap',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      colorExpression: {
        label: 'Color',
        type: 'ColorExpression',
      },
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
        helpText: 'Use Query Chart action to query this externally.',
      },
      category1Attribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => ['String'],
        label: 'Category 1 Attribute',
        type: 'ComboAttributeChooser',
      },
      category2Attribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => ['String'],
        label: 'Category 2 Attribute',
        type: 'ComboAttributeChooser',
      },
      category3Attribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => ['String'],
        label: 'Category 3 Attribute',
        type: 'ComboAttributeChooser',
      },
      labelAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => ['String'],
        label: 'Label Attribute',
        required: true,
        type: 'ComboAttributeChooser',
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => [
          'Integer',
          'Decimal',
          'Double',
          'Percent',
        ],
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
      },
      functionType: {
        category: 'Data',
        label: 'Value Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      chartWidth: {
        label: 'Width',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's width. i.e. a value of 1 takes 100% of the container's width.",
      },
      chartHeight: {
        label: 'Height',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's height. i.e. a value of 1 takes 100% of the container's height.",
      },
      preRenderFunction: {
        label: 'Pre-render Callback',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText:
          'Use this callback function to customize the chart by manipulating the `data` & `options` passed to this function. Refer https://www.chartjs.org/docs/latest/configuration/ for various options you could use to customize the charts.',
      },
      spacing: {
        label: 'Spacing',
        max: 8,
        min: 0,
        step: 0.5,
        type: 'number',
        helpText: 'Space between tiles',
      },
      borderWidth: {
        label: 'Border Width',
        max: 3,
        min: 0,
        step: 0.5,
        type: 'number',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'category1Attribute':
        case 'category2Attribute':
        case 'category3Attribute':
        case 'labelAttribute':
        case 'valueAttribute':
        case 'functionType':
          return !!itemData.dsalias;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    getDisplayText: (item) => item.props.labelAttribute ?? '',
    category: 'Dashboard',
    icon: { iconName: 'chart-line' },
    appendOverlay: true,
    description: `**Overview**<br/>
The Chart JS Treemap is a specialized visualization component designed to display hierarchical data as a set of nested rectangles. Each branch of the hierarchy is given a colored rectangle, which is then tiled with smaller rectangles representing sub-branches.
The area of each rectangle is proportional to its numeric value, allowing users to instantly spot the largest contributors (e.g., "Which region has the highest sales?") or identifying patterns across complex categories without reading rows of data.

**1. Data Configuration**<br/>
The Treemap relies on aggregation to determine the size of its tiles. You must map your data attributes to the visual properties of the chart.
* **DataSource Alias:** Select the data source containing your records.
* **Label Attribute:** The text displayed inside each tile (e.g., "State Name").
* **Value Attribute:** The numeric field used to calculate the area of the tile (e.g., "Total Revenue").
* **Value Function:** Defines how the system aggregates multiple records into a single tile size. (Options: Sum, Count, Average, Max, Min).

**2. Hierarchical Grouping**<br/>
To create a true nested Treemap, you can define up to three levels of categorization. This allows users to drill down visually from broad groups to specific details.
* **Category 1 Attribute:** The top-level grouping (e.g., "Region").
* **Category 2 Attribute:** The second level, nested inside the first (e.g., "Division").
* **Category 3 Attribute:** The deepest level of grouping (e.g., "State").

**3. Visual Styling & Coloring**<br/>
* **Color:** A color expression field. You can set a static hex code or use logic to color tiles dynamically (e.g., making underperforming regions Red and high-performing regions Green).
* **Spacing:** Controls the gap between tiles, helping to visually separate categories.
* **Border Width:** Sets the thickness of the tile outlines.

**4. Sizing & Layout**<br/>
This component features a unique sizing logic that adapts to both fixed and responsive layouts.
* **Width / Height:** * **Values > 1:** Treated as pixels (e.g., 400 = 400px).
    * **Values ≤ 1:** Treated as a percentage of the parent container (e.g., 1 = 100%, 0.5 = 50%).

**5. Advanced Customization**<br/>
* **Pre-render Callback:** A controller function for developers. This gives you direct access to the Chart.js configuration object before the chart draws. Use this to modify tooltips, legends, or animation settings using the full Chart.js API.
* **Do Not Prefetch:** Optimization setting. Enable this if you are loading massive datasets and want to defer data fetching until the specific view is active.

**6. Interaction**<br/>
* **TriggerOnClick:** The component allows you to add a TriggerOnClick child. This executes a workflow when a specific tile is clicked, allowing for "drill-down" interactions (e.g., clicking a "California" tile to open a detailed list of California sales).`,
    helpRenders: [
      {
        title: 'Basic Treemap',
        description: 'Try changing the color, spacing & border width!',
        page: SamplePage,
        exampleId: 'eg-01FYJX6X39XF6ZJBJ2SWGA63E6',
        clearParentChildren: true,
        height: 380,
        visibleProps: ['spacing', 'borderWidth', 'colorExpression'],
        item: {
          type: 'ChartJSTreemap',
          props: {
            dsalias: 'dataAlias',
            spacing: 2,
            borderWidth: 1,
            functionType: 'Sum',
            doNotPrefetch: true,
            labelAttribute: 'state',
            valueAttribute: 'area',
            colorExpression: {
              type: 'text',
              value: 'rgba(80,227,194,1)',
            },
          },
          itemId: 'chart',
          parent: 'flexItem',
          children: ['triggerOnClick'],
        },
      },
      {
        title: 'Multi-level Treemap',
        description: 'Use the Category 1-3 attributes to display nested levels',
        page: SamplePage,
        exampleId: 'eg-01FYJXBQ9C4GPW0BDV1D902KM4',
        clearParentChildren: true,
        height: 480,
        visibleProps: [
          'category1Attribute',
          'category2Attribute',
          'category3Attribute',
        ],
        item: {
          type: 'ChartJSTreemap',
          props: {
            dsalias: 'dataAlias',
            spacing: 2,
            borderWidth: 1,
            functionType: 'Sum',
            doNotPrefetch: true,
            labelAttribute: 'state',
            valueAttribute: 'area',
            colorExpression: {
              type: 'text',
              value: 'rgba(80,227,194,1)',
            },
            category1Attribute: 'region',
            category2Attribute: 'division',
            category3Attribute: 'state',
          },
          itemId: 'chart',
          parent: 'flexItem',
          children: ['triggerOnClick'],
        },
      },
    ],
  };
};

export default ChartJSTreemapManager;
